import { useSwarm } from "../SwarmContext"
import LoadingSpinner from "./LoadingSpinner"
import ErrorMessage from "./ErrorMessage"
import SectionHeader from "./SectionHeader"
import { createResource, createSignal, Show, createEffect } from "solid-js"
import Scrollable from "./Scrollable"
import apiv1, { LeaderboardEntry, GetPeerResult } from "../swarm.apiv1"
import { Button } from "../ui/Button"

export default function Leaderboard() {
	const { leaders, leadersLoading, leadersError } = useSwarm()

	let containerRef: HTMLDivElement | undefined

	// Search state: input is the raw text from the <input>, but query is what is searched for.
	// This only exists in two signals so that we search on submit, not on each keystroke.
	const [searchInput, setSearchInput] = createSignal("")
	const [leaderSearchQuery, setLeaderSearchQuery] = createSignal<string | null>(null)
	const [leaderSearchResultError, setLeaderSearchResultError] = createSignal<Error | null>(null)
	const [searchTrigger, setSearchTrigger] = createSignal(0)

	const [searchingForPeer, setSearchingForPeer] = createSignal<boolean>(false)

	type SearchResult = {
		index: number
		leader?: LeaderboardEntry
		peer?: GetPeerResult
		inLeaderboard: boolean
	}

	// This is a little hacky, but I want to allow triggering another search even if the leaderSearchQuery hasn't changed.
	// The searchTrigger() signal is incremented with each search, so we can always re-fire the search.
	const [leaderSearchResult] = createResource(
		() => ({
			query: leaderSearchQuery(),
			trigger: searchTrigger(),
			leaders: leaders(),
		}),
		async ({ query, leaders }) => {
			setLeaderSearchResultError(null)

			if (!query || query.length === 0) {
				return null
			}

			const index = leaders?.entries.findIndex((leader) => {
				const qlc = query.toLowerCase()
				return leader.peerName.toLowerCase() === qlc || leader.peerId.toLowerCase() === qlc
			})

			// Found in the leaderboard, so no need to query the API.
			if (index !== undefined && index !== null && index >= 0) {
				return {
					index,
					leader: leaders?.entries[index],
					inLeaderboard: true,
				} as SearchResult
			}

			// We need to query the API for the peer.

			setSearchingForPeer(true)

			let peer: GetPeerResult | null = null
			const peerGetter = query.toLowerCase().startsWith("qm") ? (id: string) => apiv1.getPeerById(id) : (name: string) => apiv1.getPeerByName(name)

			try {
				peer = await peerGetter(query)
			} catch (error) {
				setLeaderSearchResultError(new Error(`error searching for peer ${query}`))
				setSearchingForPeer(false)
				return null
			}

			if (!peer) {
				setLeaderSearchResultError(new Error(`peer ${query} not found.`))
				setSearchingForPeer(false)
				return null
			}

			setSearchingForPeer(false)
			return {
				index: -1,
				peer: peer,
				inLeaderboard: false,
			} as SearchResult
		},
	)

	const searchLeaderboard = (e: SubmitEvent) => {
		e.preventDefault()

		// This will trigger the refetch for the leaderSearchResult.
		setLeaderSearchQuery(searchInput())
		setSearchTrigger((prev) => prev + 1)
	}

	const twoDecimalPlaces = (value: number) => {
		return value.toFixed(2)
	}

	/**
	 * Checks if the leader is the searched leader.
	 * @param leader - The leader to check.
	 * @returns True if the leader is the searched leader, false otherwise.
	 */
	const isSearchedLeader = (target: LeaderboardEntry) => {
		if (!leaderSearchResult()) {
			return false
		}
		const leader = leaderSearchResult()?.leader
		if (!leader) {
			return false
		}
		const matchId = leader.peerId.toLowerCase() === target.peerId.toLowerCase()
		const matchName = leader.peerName.toLowerCase() === target.peerName.toLowerCase()

		return matchId || matchName
	}

	const scrollToLeader = (index: number) => {
		if (!containerRef) {
			return
		}

		let itemPath = "[data-testid='off-leaderboard-peer']"

		if (index >= 0) {
			itemPath = `[data-testid="leader-${leaders()?.entries[index].peerId}"]`
		}

		const leaderElement = containerRef.querySelector(itemPath)
		if (!leaderElement) {
			return
		}

		// Check if scrollIntoView is available before calling it
		if (typeof leaderElement.scrollIntoView === "function") {
			leaderElement.scrollIntoView({
				behavior: "smooth",
				block: "center",
			})
		} else {
			// Fallback for environments where scrollIntoView is not available
			console.log(`Scrolling to leader at index ${index} (scrollIntoView not available)`)
		}
	}

	// Watch for search results and scroll when found
	createEffect(() => {
		if (leaderSearchResult()?.inLeaderboard) {
			scrollToLeader(leaderSearchResult()!.index)
			return
		}

		if (leaderSearchResult()?.peer) {
			scrollToLeader(-1)
			return
		}
	})

	// Only show spinner on the first render.
	// Otherwise we silently refresh.
	if (leadersLoading() && leaders()?.entries.length === 0) {
		return <LoadingSpinner message="Fetching leaders" />
	}

	if (leadersError()) {
		return <ErrorMessage message="Failed to fetch leaderboard data" />
	}

	// TODO: Only used when displaying rewards.
	// const twoDecimalPlaces = (value: number) => {
	// 	return value.toFixed(2)
	// }

	const LeaderboardTooltip = () => {
		return (
			<div class="uppercase">
				<p class="mb-4">RL Swarm is a three-stage game where agents first solve problems individually, then evaluate each other's solutions, and finally reach consensus on the best answer. The leaderboard tracks their participation (how many rounds and stages they complete) and their training rewards (how good their answers are).</p>
				<p>
					<strong>Move your agent up the leaderboard</strong> by participating consistently and running models that produce the best answers.
				</p>
			</div>
		)
	}

	return (
		<div class="w-full">
			{/* Header + Search */}
			<div class="grid grid-cols-1 gap-2">
				<SectionHeader title="Leaderboard" tooltip={LeaderboardTooltip()} />

				<div class="relative grid grid-cols-2 gap-2">
					<form onSubmit={searchLeaderboard} class="flex uppercase mt-2 mb-2 uppercase" inert={leaderSearchResult.loading || searchingForPeer()}>
						<input type="text" value={searchInput()} onInput={(e) => setSearchInput(e.currentTarget.value)} placeholder="ENTER A NODE NAME OR ID" class="border border-primary-content p-2 flex-grow focus:outline-none focus:ring-0 focus:border-accent bg-info text-info-content" />
						<button type="submit" class="uppercase border-t border-b border-r border-primary-content p-2 bg-primary text-primary-content filter brightness-95" data-testid="leaderboard-search-button">
							<Show when={!searchingForPeer() && !leaderSearchResult.loading} fallback={<LoadingSpinner message="..." />}>
								Search
							</Show>
						</button>
					</form>
					<Show when={leaderSearchResultError()}>
						<div class="md:col-span-2 mb-2">
							<ErrorMessage message={leaderSearchResultError()?.message || "Failed to search leaderboard"} />
						</div>
					</Show>
				</div>
			</div>

			<div class="relative">
				<Scrollable class="max-h-[270px] border-2 border-dotted border-base-content">
					<div ref={containerRef}>
						<table class="min-w-full table-auto border-base-content border-collapse border-dotted border-separate border-spacing-1 border-spacing-x-0 py-1 px-4 bg-info text-info-content">
							<thead>
								<tr class="align-top">
									<th class="font-normal text-left w-[4em] relative">
										Rank
										<span class="absolute bottom-0 left-0 w-[90%] border-b border-dotted"></span>
									</th>
									{/* GH Issue 91: Re-enable this column
									<th class="font-normal text-left w-[6em] relative">
										Online
										<span class="absolute bottom-0 left-0 w-[90%] border-b border-dotted"></span>
									</th>
									*/}
									<th class="font-normal text-left w-auto relative">
										Name
										<span class="absolute bottom-0 left-0 w-[90%] border-b border-dotted"></span>
									</th>
									<th class="font-normal text-left w-auto pr-4 relative">
										Participation
										<span class="absolute bottom-0 left-0 w-[90%] border-b border-dotted"></span>
									</th>
									{/* TODO: Toggle this column with feature flagger */}
									<th class="font-normal text-left w-auto relative hidden md:table-cell">
										Training&nbsp;Rewards
										<span class="absolute bottom-0 left-0 w-[100%] border-b border-dotted"></span>
									</th>
								</tr>
							</thead>
							<tbody class="uppercase">
								{leaders()?.entries.map((leader, index) => (
									<tr classList={{ "bg-accent text-white": isSearchedLeader(leader) }} data-testid={`leader-${leader.peerId}`}>
										{/* Rank */}
										<td class="font-normal text-left w-auto relative" data-testid="rank-cell">
											{index + 1}
										</td>

										{/* Online */}
										{/* GH Issue 91: Re-enable this column
										<td class="text-right" data-column="online">
											<div class="pr-4">{leader.online ? "[•]" : "[ ]"}</div>
										</td>
										*/}

										{/* Name */}
										<td class="font-normal text-left w-auto relative" data-testid="name-cell">
											<div class="relative group">
												{leader.peerName || leader.peerId}
												<span class="text-xs leading-normal w-full uppercase absolute left-0 top-0 bg-info text-info-content opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap cursor-default pr-4">{leader.peerId}</span>
											</div>
										</td>

										{/* Participation */}
										<td class="font-normal text-left w-auto relative" data-testid="participation-cell">
											{leader.score}
										</td>

										{/* TODO: Toggle this column with feature flagger */}
										<td class="text-right hidden md:table-cell" data-column="reward-cell">
											{twoDecimalPlaces(leader.reward)}
										</td>
									</tr>
								))}
								<Show when={leaderSearchResult()?.peer}>
									<tr class="bg-accent text-white" data-testid="off-leaderboard-peer">
										<td class="font-normal text-left w-auto relative" data-testid="rank-cell-off">
											&gt;100
										</td>
										{/* GH Issue 91: Re-enable this column
										<td class="text-right" data-column="online-off">
											<div class="pr-4">{leaderSearchResult()?.peer?.online ? "[•]" : "[ ]"}</div>
										</td>
										*/}
										<td class="font-normal text-left w-auto relative" data-testid="name-cell-off">
											{leaderSearchResult()?.peer?.peerName}
										</td>
										<td class="font-normal text-left w-auto relative" data-testid="participation-cell-off">
											{leaderSearchResult()?.peer?.score}
										</td>
										{/* TODO: Toggle this column with feature flagger */}
										<td class="font-normal text-right hidden md:table-cell" data-testid="reward-cell-off">
											{twoDecimalPlaces(leaderSearchResult()?.peer?.reward || 0)}
										</td>
									</tr>
								</Show>
							</tbody>
						</table>
					</div>
				</Scrollable>
			</div>
		</div>
	)
}
